import React, { useState } from 'react';
import { Star, Shield, Zap, Clock, CheckCircle2, Users, TrendingUp, Search, Filter, X, Bot, Briefcase, Award, MessageSquare, ChevronRight, Sparkles, Eye, UserPlus, ArrowRight, Settings, Sliders, Plus, Trash2, GripVertical, ToggleLeft, ToggleRight, Save, RotateCcw, Brain, Target, Bell, Gauge, Pencil, Check, Activity, SearchCheck, BarChart3, Lock, FileText, Rocket, Palette, Blocks, Scale, ClipboardList, CircleDot, Layers } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
interface Skill {
  name: string;
  level: number; // 0-100
  enabled: boolean;
  category: 'core' | 'advanced' | 'custom';
}
interface AgentBehavior {
  proactivity: number; // 0-100
  notificationFrequency: 'realtime' | 'hourly' | 'daily' | 'manual';
  autoActions: boolean;
  detailLevel: 'concise' | 'balanced' | 'detailed';
  focusAreas: string[];
}
interface Agent {
  id: string;
  name: string;
  emoji: string;
  specialty: string;
  description: string;
  longDescription: string;
  skills: Skill[];
  rating: number;
  reviews: number;
  projectsCompleted: number;
  availability: 'available' | 'busy' | 'coming_soon';
  rate: string;
  experience: string;
  successRate: string;
  responseTime: string;
  hired: boolean;
  featured?: boolean;
  capabilities: {
    text: string;
    enabled: boolean;
  }[];
  behavior: AgentBehavior;
}
const defaultBehavior: AgentBehavior = {
  proactivity: 70,
  notificationFrequency: 'hourly',
  autoActions: false,
  detailLevel: 'balanced',
  focusAreas: []
};
const agentIconMap: Record<string, React.ElementType> = {
  'scrum-master': Activity,
  'qa-agent': SearchCheck,
  'analytics-agent': BarChart3,
  'security-agent': Lock,
  'docs-agent': FileText,
  'devops-agent': Rocket,
  'ux-agent': Palette,
  'architect-agent': Blocks
};
const agentIconColors: Record<string, string> = {
  'scrum-master': 'text-indigo-400',
  'qa-agent': 'text-rose-400',
  'analytics-agent': 'text-cyan-400',
  'security-agent': 'text-emerald-400',
  'docs-agent': 'text-amber-400',
  'devops-agent': 'text-orange-400',
  'ux-agent': 'text-violet-400',
  'architect-agent': 'text-sky-400'
};
function AgentIcon({
  agentId,
  size = 24,
  className = ''
}: {
  agentId: string;
  size?: number;
  className?: string;
}) {
  const Icon = agentIconMap[agentId] || CircleDot;
  const color = agentIconColors[agentId] || 'text-slate-400';
  return <Icon size={size} className={`${color} ${className}`} data-id="element-3248" />;
}
const mockAgents: Agent[] = [{
  id: 'scrum-master',
  name: 'Scrum Master Pro',
  emoji: 'icon',
  specialty: 'Agile & Sprint Management',
  description: 'Experto en ceremonias ágiles, planificación de sprints y optimización de velocidad del equipo.',
  longDescription: 'Agente especializado en gestión ágil con experiencia en Scrum, Kanban y SAFe. Facilita ceremonies, identifica bloqueadores proactivamente, y optimiza la velocidad del equipo usando análisis predictivo.',
  skills: [{
    name: 'Sprint Planning',
    level: 95,
    enabled: true,
    category: 'core'
  }, {
    name: 'Retrospectives',
    level: 90,
    enabled: true,
    category: 'core'
  }, {
    name: 'Velocity Analysis',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'Burndown Charts',
    level: 92,
    enabled: true,
    category: 'core'
  }, {
    name: 'Blocker Detection',
    level: 85,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Workload Balancing',
    level: 80,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Risk Prediction',
    level: 75,
    enabled: false,
    category: 'advanced'
  }, {
    name: 'Team Health Metrics',
    level: 70,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.9,
  reviews: 342,
  projectsCompleted: 156,
  availability: 'available',
  rate: '$0.02/msg',
  experience: '2+ años',
  successRate: '97%',
  responseTime: '< 2s',
  hired: true,
  featured: true,
  capabilities: [{
    text: 'Planificación automática de sprints basada en velocidad histórica',
    enabled: true
  }, {
    text: 'Facilitación de daily standups asincrónicos',
    enabled: true
  }, {
    text: 'Detección proactiva de bloqueadores y riesgos',
    enabled: true
  }, {
    text: 'Generación de reportes de sprint y retrospectivas',
    enabled: true
  }, {
    text: 'Recomendaciones de asignación basadas en workload',
    enabled: false
  }, {
    text: 'Predicción de fechas de entrega con ML',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    proactivity: 80,
    focusAreas: ['sprints', 'blockers', 'velocity']
  }
}, {
  id: 'qa-agent',
  name: 'QA Inspector',
  emoji: 'icon',
  specialty: 'Quality Assurance & Testing',
  description: 'Detección de bugs, automatización de tests y gates de calidad para cada release.',
  longDescription: 'Agente de QA que analiza código, detecta patrones de bugs recurrentes, sugiere test cases y monitorea la calidad del código en cada PR.',
  skills: [{
    name: 'Bug Detection',
    level: 92,
    enabled: true,
    category: 'core'
  }, {
    name: 'Test Automation',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'Code Review',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Regression Testing',
    level: 90,
    enabled: true,
    category: 'core'
  }, {
    name: 'E2E Testing',
    level: 78,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Performance Testing',
    level: 70,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.8,
  reviews: 218,
  projectsCompleted: 89,
  availability: 'available',
  rate: '$0.03/msg',
  experience: '1.5 años',
  successRate: '94%',
  responseTime: '< 3s',
  hired: false,
  capabilities: [{
    text: 'Análisis estático de código para detectar vulnerabilidades',
    enabled: true
  }, {
    text: 'Generación automática de test cases',
    enabled: true
  }, {
    text: 'Monitoreo de cobertura de tests',
    enabled: true
  }, {
    text: 'Reporte de regresiones en cada PR',
    enabled: true
  }, {
    text: 'Sugerencias de mejora de calidad',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['bugs', 'testing']
  }
}, {
  id: 'analytics-agent',
  name: 'Data Analyst',
  emoji: 'icon',
  specialty: 'Metrics & Business Intelligence',
  description: 'Análisis de métricas, reportes ejecutivos y visualización de datos del proyecto.',
  longDescription: 'Transforma datos crudos del proyecto en insights accionables. Genera dashboards, identifica tendencias y predice riesgos basándose en datos históricos.',
  skills: [{
    name: 'KPI Tracking',
    level: 90,
    enabled: true,
    category: 'core'
  }, {
    name: 'Dashboard Creation',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'Trend Analysis',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Predictive Analytics',
    level: 75,
    enabled: false,
    category: 'advanced'
  }, {
    name: 'Data Visualization',
    level: 82,
    enabled: true,
    category: 'advanced'
  }],
  rating: 4.7,
  reviews: 167,
  projectsCompleted: 73,
  availability: 'available',
  rate: '$0.02/msg',
  experience: '1 año',
  successRate: '91%',
  responseTime: '< 4s',
  hired: false,
  capabilities: [{
    text: 'Dashboards automáticos con KPIs en tiempo real',
    enabled: true
  }, {
    text: 'Predicción de fechas de entrega con ML',
    enabled: false
  }, {
    text: 'Análisis de tendencias de productividad',
    enabled: true
  }, {
    text: 'Reportes ejecutivos semanales automáticos',
    enabled: true
  }, {
    text: 'Alertas inteligentes por desviaciones',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['metrics', 'reports']
  }
}, {
  id: 'security-agent',
  name: 'Security Guardian',
  emoji: 'icon',
  specialty: 'Security & Compliance',
  description: 'Escaneo de vulnerabilidades, compliance OWASP y auditoría de seguridad continua.',
  longDescription: 'Agente de seguridad que monitorea continuamente el código y la infraestructura, detecta vulnerabilidades y asegura compliance con estándares de la industria.',
  skills: [{
    name: 'OWASP Scanning',
    level: 95,
    enabled: true,
    category: 'core'
  }, {
    name: 'Vulnerability Detection',
    level: 92,
    enabled: true,
    category: 'core'
  }, {
    name: 'Compliance Audit',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'Secret Scanning',
    level: 90,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Penetration Testing',
    level: 70,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.9,
  reviews: 134,
  projectsCompleted: 61,
  availability: 'busy',
  rate: '$0.05/msg',
  experience: '2 años',
  successRate: '99%',
  responseTime: '< 2s',
  hired: false,
  featured: true,
  capabilities: [{
    text: 'Escaneo continuo de vulnerabilidades OWASP Top 10',
    enabled: true
  }, {
    text: 'Auditoría automática de dependencias',
    enabled: true
  }, {
    text: 'Verificación de compliance (SOC2, GDPR, HIPAA)',
    enabled: true
  }, {
    text: 'Análisis de secrets expuestos en código',
    enabled: true
  }, {
    text: 'Recomendaciones de hardening',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    proactivity: 90,
    focusAreas: ['security', 'compliance']
  }
}, {
  id: 'docs-agent',
  name: 'Doc Writer',
  emoji: 'icon',
  specialty: 'Documentation & Knowledge Base',
  description: 'Documentación de APIs, decision logs y mantenimiento de knowledge base.',
  longDescription: 'Genera y mantiene documentación técnica actualizada automáticamente. Crea decision logs, documenta APIs y mantiene la knowledge base del equipo.',
  skills: [{
    name: 'API Documentation',
    level: 90,
    enabled: true,
    category: 'core'
  }, {
    name: 'Decision Logs',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Wiki Management',
    level: 80,
    enabled: true,
    category: 'core'
  }, {
    name: 'Changelog Generation',
    level: 88,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Onboarding Guides',
    level: 75,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.6,
  reviews: 98,
  projectsCompleted: 45,
  availability: 'available',
  rate: '$0.01/msg',
  experience: '1 año',
  successRate: '88%',
  responseTime: '< 5s',
  hired: false,
  capabilities: [{
    text: 'Generación automática de documentación de API',
    enabled: true
  }, {
    text: 'Decision logs con contexto y rationale',
    enabled: true
  }, {
    text: 'Mantenimiento de wikis y knowledge base',
    enabled: true
  }, {
    text: 'Changelogs automáticos por release',
    enabled: true
  }, {
    text: 'Guías de onboarding para nuevos miembros',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['docs', 'knowledge']
  }
}, {
  id: 'devops-agent',
  name: 'DevOps Engineer',
  emoji: 'icon',
  specialty: 'CI/CD & Infrastructure',
  description: 'Pipelines de CI/CD, deployments automatizados y monitoreo de infraestructura.',
  longDescription: 'Gestiona pipelines de CI/CD, automatiza deployments, monitorea la salud de la infraestructura y optimiza costos cloud.',
  skills: [{
    name: 'CI/CD Pipelines',
    level: 92,
    enabled: true,
    category: 'core'
  }, {
    name: 'Docker & Containers',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'AWS Management',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Monitoring & Alerts',
    level: 80,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Infrastructure as Code',
    level: 78,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.8,
  reviews: 156,
  projectsCompleted: 82,
  availability: 'available',
  rate: '$0.04/msg',
  experience: '1.5 años',
  successRate: '96%',
  responseTime: '< 3s',
  hired: false,
  capabilities: [{
    text: 'Configuración automática de pipelines CI/CD',
    enabled: true
  }, {
    text: 'Deployment zero-downtime con rollback',
    enabled: true
  }, {
    text: 'Monitoreo de salud de infraestructura',
    enabled: true
  }, {
    text: 'Optimización de costos cloud',
    enabled: false
  }, {
    text: 'Alertas y auto-scaling inteligente',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['deployments', 'infra']
  }
}, {
  id: 'ux-agent',
  name: 'UX Reviewer',
  emoji: 'icon',
  specialty: 'Design & Accessibility',
  description: 'Reviews de diseño, auditorías de accesibilidad y análisis de experiencia de usuario.',
  longDescription: 'Analiza interfaces, detecta problemas de usabilidad, verifica compliance de accesibilidad WCAG y sugiere mejoras basadas en heurísticas de diseño.',
  skills: [{
    name: 'Accessibility Audit',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Heuristic Analysis',
    level: 80,
    enabled: true,
    category: 'core'
  }, {
    name: 'User Flow Review',
    level: 78,
    enabled: true,
    category: 'core'
  }, {
    name: 'Design System Check',
    level: 75,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.5,
  reviews: 76,
  projectsCompleted: 34,
  availability: 'coming_soon',
  rate: '$0.03/msg',
  experience: '6 meses',
  successRate: '85%',
  responseTime: '< 6s',
  hired: false,
  capabilities: [{
    text: 'Auditoría automática de accesibilidad WCAG 2.1',
    enabled: true
  }, {
    text: 'Análisis heurístico de interfaces',
    enabled: true
  }, {
    text: 'Detección de inconsistencias en design system',
    enabled: false
  }, {
    text: 'Sugerencias de mejora de UX basadas en datos',
    enabled: false
  }, {
    text: 'Review de flujos de usuario',
    enabled: true
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['a11y', 'ux']
  }
}, {
  id: 'architect-agent',
  name: 'System Architect',
  emoji: 'icon',
  specialty: 'Architecture & Tech Debt',
  description: 'Diseño de sistemas, análisis de escalabilidad y gestión de deuda técnica.',
  longDescription: 'Evalúa la arquitectura del sistema, identifica tech debt, propone mejoras de escalabilidad y documenta decisiones arquitectónicas (ADRs).',
  skills: [{
    name: 'System Design',
    level: 90,
    enabled: true,
    category: 'core'
  }, {
    name: 'Scalability Analysis',
    level: 85,
    enabled: true,
    category: 'core'
  }, {
    name: 'Tech Debt Tracking',
    level: 88,
    enabled: true,
    category: 'core'
  }, {
    name: 'ADR Documentation',
    level: 82,
    enabled: true,
    category: 'advanced'
  }, {
    name: 'Microservices Design',
    level: 78,
    enabled: false,
    category: 'advanced'
  }],
  rating: 4.7,
  reviews: 112,
  projectsCompleted: 48,
  availability: 'busy',
  rate: '$0.06/msg',
  experience: '2 años',
  successRate: '93%',
  responseTime: '< 4s',
  hired: false,
  capabilities: [{
    text: 'Análisis de arquitectura y recomendaciones',
    enabled: true
  }, {
    text: 'Detección y priorización de tech debt',
    enabled: true
  }, {
    text: 'Architecture Decision Records (ADRs)',
    enabled: true
  }, {
    text: 'Evaluación de escalabilidad y performance',
    enabled: false
  }, {
    text: 'Diseño de microservicios y event-driven',
    enabled: false
  }],
  behavior: {
    ...defaultBehavior,
    focusAreas: ['architecture', 'tech-debt']
  }
}];
const availabilityConfig = {
  available: {
    label: 'Disponible',
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/50 border-emerald-900/50',
    dot: 'bg-emerald-400'
  },
  busy: {
    label: 'Ocupado',
    color: 'text-amber-400',
    bg: 'bg-amber-950/50 border-amber-900/50',
    dot: 'bg-amber-400'
  },
  coming_soon: {
    label: 'Próximamente',
    color: 'text-slate-400',
    bg: 'bg-slate-800 border-slate-700',
    dot: 'bg-slate-500'
  }
};
export function AgentMarketplace() {
  const [agents, setAgents] = useState<Agent[]>(mockAgents);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAvailability, setFilterAvailability] = useState<'all' | 'available' | 'hired'>('all');
  const [hiring, setHiring] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'profile' | 'skills' | 'behavior'>('profile');
  const [newSkillName, setNewSkillName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const filteredAgents = agents.filter(a => {
    const matchesSearch = !searchQuery || a.name.toLowerCase().includes(searchQuery.toLowerCase()) || a.specialty.toLowerCase().includes(searchQuery.toLowerCase()) || a.skills.some(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filterAvailability === 'all' || filterAvailability === 'available' && a.availability === 'available' || filterAvailability === 'hired' && a.hired;
    return matchesSearch && matchesFilter;
  });
  const hiredCount = agents.filter(a => a.hired).length;
  const handleHire = (agentId: string) => {
    setHiring(agentId);
    setTimeout(() => {
      setAgents(prev => prev.map(a => a.id === agentId ? {
        ...a,
        hired: true
      } : a));
      setHiring(null);
      if (selectedAgent?.id === agentId) setSelectedAgent(prev => prev ? {
        ...prev,
        hired: true
      } : null);
    }, 1200);
  };
  const handleFire = (agentId: string) => {
    setAgents(prev => prev.map(a => a.id === agentId ? {
      ...a,
      hired: false
    } : a));
    if (selectedAgent?.id === agentId) setSelectedAgent(prev => prev ? {
      ...prev,
      hired: false
    } : null);
    setDetailTab('profile');
  };
  const toggleSkill = (agentId: string, skillName: string) => {
    setAgents(prev => prev.map(a => {
      if (a.id !== agentId) return a;
      return {
        ...a,
        skills: a.skills.map(s => s.name === skillName ? {
          ...s,
          enabled: !s.enabled
        } : s)
      };
    }));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        skills: prev.skills.map(s => s.name === skillName ? {
          ...s,
          enabled: !s.enabled
        } : s)
      } : null);
    }
  };
  const updateSkillLevel = (agentId: string, skillName: string, level: number) => {
    setAgents(prev => prev.map(a => {
      if (a.id !== agentId) return a;
      return {
        ...a,
        skills: a.skills.map(s => s.name === skillName ? {
          ...s,
          level
        } : s)
      };
    }));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        skills: prev.skills.map(s => s.name === skillName ? {
          ...s,
          level
        } : s)
      } : null);
    }
  };
  const toggleCapability = (agentId: string, capText: string) => {
    setAgents(prev => prev.map(a => {
      if (a.id !== agentId) return a;
      return {
        ...a,
        capabilities: a.capabilities.map(c => c.text === capText ? {
          ...c,
          enabled: !c.enabled
        } : c)
      };
    }));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        capabilities: prev.capabilities.map(c => c.text === capText ? {
          ...c,
          enabled: !c.enabled
        } : c)
      } : null);
    }
  };
  const addCustomSkill = (agentId: string) => {
    if (!newSkillName.trim()) return;
    const newSkill: Skill = {
      name: newSkillName,
      level: 50,
      enabled: true,
      category: 'custom'
    };
    setAgents(prev => prev.map(a => a.id === agentId ? {
      ...a,
      skills: [...a.skills, newSkill]
    } : a));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        skills: [...prev.skills, newSkill]
      } : null);
    }
    setNewSkillName('');
  };
  const removeSkill = (agentId: string, skillName: string) => {
    setAgents(prev => prev.map(a => a.id === agentId ? {
      ...a,
      skills: a.skills.filter(s => s.name !== skillName)
    } : a));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        skills: prev.skills.filter(s => s.name !== skillName)
      } : null);
    }
  };
  const updateBehavior = (agentId: string, updates: Partial<AgentBehavior>) => {
    setAgents(prev => prev.map(a => a.id === agentId ? {
      ...a,
      behavior: {
        ...a.behavior,
        ...updates
      }
    } : a));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(prev => prev ? {
        ...prev,
        behavior: {
          ...prev.behavior,
          ...updates
        }
      } : null);
    }
  };
  const handleSaveConfig = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };
  return <div className="h-full flex-1 w-full flex animate-in fade-in duration-300" data-id="element-3249">
      {/* Main List */}
      <div className={`flex-1 overflow-y-auto p-8 transition-all duration-300 ${selectedAgent ? 'pr-4' : ''}`} data-id="element-3250">
        <div className="max-w-5xl mx-auto space-y-6" data-id="element-3251">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-indigo-950/80 via-slate-900 to-violet-950/50 rounded-2xl p-8 border border-indigo-900/30" data-id="element-3252">
            <div className="flex items-start justify-between" data-id="element-3253">
              <div data-id="element-3254">
                <div className="flex items-center gap-2 mb-3" data-id="element-3255">
                  <Bot size={20} className="text-indigo-400" data-id="element-3256" />
                  <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider" data-id="element-3257">
                    Agent Marketplace
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-slate-100 mb-2" data-id="element-3258">
                  Contrata y configura agentes IA
                </h2>
                <p className="text-slate-400 max-w-xl" data-id="element-3259">
                  Explora agentes especializados, configura sus skills y
                  comportamiento. Cada agente se adapta a las necesidades de tu
                  equipo.
                </p>
              </div>
              <div className="text-right shrink-0 ml-8" data-id="element-3260">
                <div className="bg-slate-800/80 rounded-xl p-4 border border-slate-700" data-id="element-3261">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-3262">Tu equipo</p>
                  <p className="text-3xl font-bold text-indigo-400" data-id="element-3263">
                    {hiredCount}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-3264">agentes activos</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="flex items-center gap-4" data-id="element-3265">
            <div className="flex-1 relative" data-id="element-3266">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-3267" />

              <input className="w-full rounded-lg border border-slate-700 bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="Buscar por nombre, especialidad o skill..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3268" />
            </div>
            <div className="flex gap-2" data-id="element-3269">
              {[{
              key: 'all' as const,
              label: 'Todos',
              count: agents.length
            }, {
              key: 'available' as const,
              label: 'Disponibles',
              count: agents.filter(a => a.availability === 'available').length
            }, {
              key: 'hired' as const,
              label: 'Contratados',
              count: hiredCount
            }].map(({
              key,
              label,
              count
            }) => <button key={key} onClick={() => setFilterAvailability(key)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${filterAvailability === key ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-500/50' : 'bg-slate-900 text-slate-500 border border-slate-800 hover:border-slate-700'}`} data-id="element-3270">
                  {label}
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${filterAvailability === key ? 'bg-indigo-800 text-indigo-200' : 'bg-slate-800 text-slate-500'}`} data-id="element-3271">
                    {count}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Agent Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3272">
            {filteredAgents.map(agent => {
            const avail = availabilityConfig[agent.availability];
            const enabledSkills = agent.skills.filter(s => s.enabled).length;
            return <div key={agent.id} onClick={() => {
              setSelectedAgent(agent);
              setDetailTab('profile');
            }} className={`relative bg-slate-900 rounded-xl border p-5 cursor-pointer transition-all hover:border-slate-600 group ${selectedAgent?.id === agent.id ? 'border-indigo-500 ring-1 ring-indigo-500/30' : agent.hired ? 'border-emerald-800/50' : 'border-slate-800'}`} data-id="element-3273">
                  {agent.featured && !agent.hired && <div className="absolute top-3 right-3" data-id="element-3274">
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-950/50 text-amber-400 border border-amber-900/50 rounded-full text-[10px] font-semibold" data-id="element-3275">
                        <Sparkles size={10} data-id="element-3276" /> Featured
                      </span>
                    </div>}
                  {agent.hired && <div className="absolute top-3 right-3" data-id="element-3277">
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-950/50 text-emerald-400 border border-emerald-900/50 rounded-full text-[10px] font-semibold" data-id="element-3278">
                        <CheckCircle2 size={10} data-id="element-3279" /> En tu equipo
                      </span>
                    </div>}

                  <div className="flex items-start gap-4" data-id="element-3280">
                    <div className="w-14 h-14 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0 group-hover:border-slate-600 transition-colors" data-id="element-3281">
                      <AgentIcon agentId={agent.id} size={26} data-id="element-3282" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-3283">
                      <div className="flex items-center gap-2 mb-1" data-id="element-3284">
                        <h3 className="font-bold text-slate-100 group-hover:text-indigo-400 transition-colors" data-id="element-3285">
                          {agent.name}
                        </h3>
                      </div>
                      <p className="text-xs text-indigo-400/80 font-medium mb-2" data-id="element-3286">
                        {agent.specialty}
                      </p>
                      <p className="text-sm text-slate-400 line-clamp-2 mb-3" data-id="element-3287">
                        {agent.description}
                      </p>

                      <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-3288">
                        {agent.skills.filter(s => s.enabled).slice(0, 4).map(skill => <span key={skill.name} className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-[10px] font-medium border border-slate-700" data-id="element-3289">
                              {skill.name}
                            </span>)}
                        {enabledSkills > 4 && <span className="px-2 py-0.5 text-slate-500 text-[10px]" data-id="element-3290">
                            +{enabledSkills - 4}
                          </span>}
                      </div>

                      <div className="flex items-center gap-4 text-xs" data-id="element-3291">
                        <span className="flex items-center gap-1 text-amber-400" data-id="element-3292">
                          <Star size={12} className="fill-amber-400" data-id="element-3293" />{' '}
                          {agent.rating}
                          <span className="text-slate-500" data-id="element-3294">
                            ({agent.reviews})
                          </span>
                        </span>
                        <span className="flex items-center gap-1 text-slate-400" data-id="element-3295">
                          <Briefcase size={12} data-id="element-3296" /> {agent.projectsCompleted}
                        </span>
                        <span className={`flex items-center gap-1.5 ${avail.color}`} data-id="element-3297">
                          <span className={`w-1.5 h-1.5 rounded-full ${avail.dot} ${agent.availability === 'available' ? 'animate-pulse' : ''}`} data-id="element-3298"></span>
                          {avail.label}
                        </span>
                        <span className="text-slate-500 ml-auto font-mono" data-id="element-3299">
                          {agent.rate}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {filteredAgents.length === 0 && <div className="text-center py-16 text-slate-500" data-id="element-3300">
              <CircleDot size={48} className="mx-auto mb-4 opacity-30" data-id="element-3301" />
              <p data-id="element-3302">No se encontraron agentes con esos criterios.</p>
            </div>}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedAgent && <div className="w-[440px] bg-slate-900 border-l border-slate-800 overflow-y-auto shrink-0 animate-in slide-in-from-right-10 duration-300 flex flex-col" data-id="element-3303">
          {/* Panel Header */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0" data-id="element-3304">
            {/* Tabs */}
            <div className="flex gap-1" data-id="element-3305">
              {[{
            key: 'profile' as const,
            label: 'Perfil',
            icon: Eye
          }, ...(selectedAgent.hired ? [{
            key: 'skills' as const,
            label: 'Skills',
            icon: Sliders
          }, {
            key: 'behavior' as const,
            label: 'Comportamiento',
            icon: Brain
          }] : [])].map(({
            key,
            label,
            icon: Icon
          }) => <button key={key} onClick={() => setDetailTab(key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${detailTab === key ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-500/50' : 'text-slate-500 hover:text-slate-300 border border-transparent'}`} data-id="element-3306">
                  <Icon size={12} data-id="element-3307" /> {label}
                </button>)}
            </div>
            <button onClick={() => setSelectedAgent(null)} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors" data-id="element-3308">
              <X size={18} data-id="element-3309" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-3310">
            {/* PROFILE TAB */}
            {detailTab === 'profile' && <>
                <div className="text-center" data-id="element-3311">
                  <div className="w-20 h-20 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto mb-4" data-id="element-3312">
                    <AgentIcon agentId={selectedAgent.id} size={36} data-id="element-3313" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-100" data-id="element-3314">
                    {selectedAgent.name}
                  </h2>
                  <p className="text-sm text-indigo-400 font-medium" data-id="element-3315">
                    {selectedAgent.specialty}
                  </p>
                  <div className="flex items-center justify-center gap-1 mt-2" data-id="element-3316">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} className={i <= Math.floor(selectedAgent.rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-700'} data-id="element-3317" />)}
                    <span className="text-sm text-slate-400 ml-1" data-id="element-3318">
                      {selectedAgent.rating} ({selectedAgent.reviews})
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3" data-id="element-3319">
                  {[{
              label: 'Proyectos',
              value: selectedAgent.projectsCompleted.toString(),
              icon: Briefcase
            }, {
              label: 'Éxito',
              value: selectedAgent.successRate,
              icon: TrendingUp
            }, {
              label: 'Respuesta',
              value: selectedAgent.responseTime,
              icon: Clock
            }].map(({
              label,
              value,
              icon: Icon
            }) => <div key={label} className="bg-slate-950 rounded-lg p-3 border border-slate-800 text-center" data-id="element-3320">
                      <Icon size={14} className="text-slate-500 mx-auto mb-1" data-id="element-3321" />
                      <p className="text-sm font-bold text-slate-100" data-id="element-3322">
                        {value}
                      </p>
                      <p className="text-[10px] text-slate-500" data-id="element-3323">{label}</p>
                    </div>)}
                </div>

                {/* Action */}
                <div data-id="element-3324">
                  {selectedAgent.hired ? <div className="space-y-2" data-id="element-3325">
                      <div className="flex items-center justify-center gap-2 py-3 bg-emerald-950/30 border border-emerald-900/50 rounded-xl text-emerald-400 text-sm font-medium" data-id="element-3326">
                        <CheckCircle2 size={16} data-id="element-3327" /> Agente en tu equipo
                      </div>
                      <div className="flex gap-2" data-id="element-3328">
                        <Button variant="outline" size="sm" className="flex-1 gap-1 text-xs" onClick={() => setDetailTab('skills')} data-id="element-3329">
                          <Sliders size={12} data-id="element-3330" /> Configurar Skills
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 gap-1 text-xs text-red-400 border-red-900/50 hover:bg-red-950/30" onClick={() => handleFire(selectedAgent.id)} data-id="element-3331">
                          <X size={12} data-id="element-3332" /> Remover
                        </Button>
                      </div>
                    </div> : selectedAgent.availability === 'coming_soon' ? <button disabled className="w-full py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-500 text-sm font-medium cursor-not-allowed" data-id="element-3333">
                      Próximamente
                    </button> : selectedAgent.availability === 'busy' ? <button className="w-full py-3 bg-amber-950/30 border border-amber-900/50 rounded-xl text-amber-400 text-sm font-medium" data-id="element-3334">
                      Lista de espera
                    </button> : <Button className="w-full gap-2" onClick={() => handleHire(selectedAgent.id)} isLoading={hiring === selectedAgent.id} data-id="element-3335">
                      <UserPlus size={16} data-id="element-3336" /> Contratar Agente
                    </Button>}
                </div>

                <div data-id="element-3337">
                  <h3 className="text-sm font-semibold text-slate-300 mb-2" data-id="element-3338">
                    Sobre este agente
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-3339">
                    {selectedAgent.longDescription}
                  </p>
                </div>

                <div data-id="element-3340">
                  <h3 className="text-sm font-semibold text-slate-300 mb-2" data-id="element-3341">
                    Skills (
                    {selectedAgent.skills.filter(s => s.enabled).length}{' '}
                    activas)
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-3342">
                    {selectedAgent.skills.map(skill => <span key={skill.name} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${skill.enabled ? 'bg-indigo-950/30 text-indigo-300 border-indigo-900/50' : 'bg-slate-800 text-slate-500 border-slate-700 line-through'}`} data-id="element-3343">
                        {skill.name}
                      </span>)}
                  </div>
                </div>

                <div data-id="element-3344">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3" data-id="element-3345">
                    Capacidades
                  </h3>
                  <div className="space-y-2" data-id="element-3346">
                    {selectedAgent.capabilities.map((cap, idx) => <div key={idx} className="flex items-start gap-2 text-sm" data-id="element-3347">
                        <CheckCircle2 size={14} className={`shrink-0 mt-0.5 ${cap.enabled ? 'text-emerald-500' : 'text-slate-700'}`} data-id="element-3348" />

                        <span className={cap.enabled ? 'text-slate-400' : 'text-slate-600'} data-id="element-3349">
                          {cap.text}
                        </span>
                      </div>)}
                  </div>
                </div>

                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3350">
                  <div className="flex items-center justify-between mb-2" data-id="element-3351">
                    <span className="text-sm text-slate-400" data-id="element-3352">Tarifa</span>
                    <span className="text-lg font-bold text-slate-100" data-id="element-3353">
                      {selectedAgent.rate}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-3354">
                    <span className="text-sm text-slate-400" data-id="element-3355">Experiencia</span>
                    <span className="text-sm font-medium text-slate-200" data-id="element-3356">
                      {selectedAgent.experience}
                    </span>
                  </div>
                </div>
              </>}

            {/* SKILLS CONFIG TAB */}
            {detailTab === 'skills' && selectedAgent.hired && <>
                <div data-id="element-3357">
                  <h3 className="text-lg font-bold text-slate-100 mb-1" data-id="element-3358">
                    Configurar Skills
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-3359">
                    Activa, desactiva y ajusta el nivel de cada habilidad.
                  </p>
                </div>

                {/* Skills by Category */}
                {['core', 'advanced', 'custom'].map(cat => {
            const catSkills = selectedAgent.skills.filter(s => s.category === cat);
            if (catSkills.length === 0 && cat !== 'custom') return null;
            const catIcon = cat === 'core' ? Target : cat === 'advanced' ? Zap : Sparkles;
            const catColor = cat === 'core' ? 'text-indigo-400' : cat === 'advanced' ? 'text-violet-400' : 'text-emerald-400';
            const CatIcon = catIcon;
            return <div key={cat} data-id="element-3360">
                      <div className="flex items-center gap-2 mb-3" data-id="element-3361">
                        <CatIcon size={14} className={catColor} data-id="element-3362" />
                        <span className={`text-xs font-semibold uppercase tracking-wider ${catColor}`} data-id="element-3363">
                          {cat === 'core' ? 'Core Skills' : cat === 'advanced' ? 'Advanced' : 'Custom'}
                        </span>
                        <div className="flex-1 h-px bg-slate-800" data-id="element-3364"></div>
                      </div>
                      <div className="space-y-3" data-id="element-3365">
                        {catSkills.map(skill => <div key={skill.name} className={`p-3 rounded-xl border transition-all ${skill.enabled ? 'bg-slate-950 border-slate-800' : 'bg-slate-950/50 border-slate-800/50 opacity-60'}`} data-id="element-3366">
                            <div className="flex items-center justify-between mb-2" data-id="element-3367">
                              <div className="flex items-center gap-2" data-id="element-3368">
                                <span className={`text-sm font-medium ${skill.enabled ? 'text-slate-200' : 'text-slate-500'}`} data-id="element-3369">
                                  {skill.name}
                                </span>
                                {skill.category === 'custom' && <button onClick={() => removeSkill(selectedAgent.id, skill.name)} className="p-0.5 text-slate-600 hover:text-red-400 transition-colors" data-id="element-3370">
                                    <Trash2 size={12} data-id="element-3371" />
                                  </button>}
                              </div>
                              <button onClick={() => toggleSkill(selectedAgent.id, skill.name)} className={`relative w-10 h-5 rounded-full transition-colors ${skill.enabled ? 'bg-indigo-600' : 'bg-slate-700'}`} data-id="element-3372">
                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${skill.enabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3373" />
                              </button>
                            </div>
                            {skill.enabled && <div className="flex items-center gap-3" data-id="element-3374">
                                <input type="range" min="0" max="100" value={skill.level} onChange={e => updateSkillLevel(selectedAgent.id, skill.name, parseInt(e.target.value))} className="flex-1 h-1.5 bg-slate-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg" data-id="element-3375" />

                                <span className="text-xs font-mono text-slate-400 w-8 text-right" data-id="element-3376">
                                  {skill.level}%
                                </span>
                              </div>}
                          </div>)}
                      </div>
                      {cat === 'custom' && <div className="mt-3 flex gap-2" data-id="element-3377">
                          <input className="flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={newSkillName} onChange={e => setNewSkillName(e.target.value)} placeholder="Nueva skill..." onKeyDown={e => e.key === 'Enter' && addCustomSkill(selectedAgent.id)} data-id="element-3378" />

                          <Button size="sm" onClick={() => addCustomSkill(selectedAgent.id)} className="gap-1 shrink-0" data-id="element-3379">
                            <Plus size={14} data-id="element-3380" /> Agregar
                          </Button>
                        </div>}
                    </div>;
          })}

                {/* Capabilities Toggles */}
                <div data-id="element-3381">
                  <h3 className="text-sm font-semibold text-slate-300 mb-3" data-id="element-3382">
                    Capacidades
                  </h3>
                  <div className="space-y-2" data-id="element-3383">
                    {selectedAgent.capabilities.map((cap, idx) => <div key={idx} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950 border border-slate-800" data-id="element-3384">
                        <span className={`text-xs flex-1 mr-3 ${cap.enabled ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-3385">
                          {cap.text}
                        </span>
                        <button onClick={() => toggleCapability(selectedAgent.id, cap.text)} className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${cap.enabled ? 'bg-emerald-600' : 'bg-slate-700'}`} data-id="element-3386">
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${cap.enabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-3387" />
                        </button>
                      </div>)}
                  </div>
                </div>

                <Button onClick={handleSaveConfig} className="w-full gap-2" data-id="element-3388">
                  {saveSuccess ? <>
                      <Check size={16} data-id="element-3389" /> Guardado!
                    </> : <>
                      <Save size={16} data-id="element-3390" /> Guardar Configuración
                    </>}
                </Button>
              </>}

            {/* BEHAVIOR TAB */}
            {detailTab === 'behavior' && selectedAgent.hired && <>
                <div data-id="element-3391">
                  <h3 className="text-lg font-bold text-slate-100 mb-1" data-id="element-3392">
                    Comportamiento
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-3393">
                    Ajusta cómo se comporta este agente en tu proyecto.
                  </p>
                </div>

                {/* Proactivity Slider */}
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3394">
                  <div className="flex items-center justify-between mb-3" data-id="element-3395">
                    <div className="flex items-center gap-2" data-id="element-3396">
                      <Target size={16} className="text-indigo-400" data-id="element-3397" />
                      <span className="text-sm font-medium text-slate-200" data-id="element-3398">
                        Proactividad
                      </span>
                    </div>
                    <span className="text-sm font-mono text-indigo-400" data-id="element-3399">
                      {selectedAgent.behavior.proactivity}%
                    </span>
                  </div>
                  <input type="range" min="0" max="100" value={selectedAgent.behavior.proactivity} onChange={e => updateBehavior(selectedAgent.id, {
              proactivity: parseInt(e.target.value)
            })} className="w-full h-2 bg-slate-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg" data-id="element-3400" />

                  <div className="flex justify-between text-[10px] text-slate-500 mt-1" data-id="element-3401">
                    <span data-id="element-3402">Reactivo</span>
                    <span data-id="element-3403">Proactivo</span>
                  </div>
                </div>

                {/* Notification Frequency */}
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3404">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3405">
                    <Bell size={16} className="text-indigo-400" data-id="element-3406" />
                    <span className="text-sm font-medium text-slate-200" data-id="element-3407">
                      Frecuencia de Notificaciones
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-3408">
                    {[{
                key: 'realtime' as const,
                label: 'Tiempo Real',
                desc: 'Cada evento'
              }, {
                key: 'hourly' as const,
                label: 'Cada Hora',
                desc: 'Resumen horario'
              }, {
                key: 'daily' as const,
                label: 'Diario',
                desc: 'Reporte diario'
              }, {
                key: 'manual' as const,
                label: 'Manual',
                desc: 'Solo al preguntar'
              }].map(({
                key,
                label,
                desc
              }) => <button key={key} onClick={() => updateBehavior(selectedAgent.id, {
                notificationFrequency: key
              })} className={`p-3 rounded-lg border text-left transition-all ${selectedAgent.behavior.notificationFrequency === key ? 'bg-indigo-900/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-3409">
                        <span className="text-xs font-semibold block" data-id="element-3410">
                          {label}
                        </span>
                        <span className="text-[10px] opacity-70" data-id="element-3411">{desc}</span>
                      </button>)}
                  </div>
                </div>

                {/* Detail Level */}
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3412">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3413">
                    <Gauge size={16} className="text-indigo-400" data-id="element-3414" />
                    <span className="text-sm font-medium text-slate-200" data-id="element-3415">
                      Nivel de Detalle
                    </span>
                  </div>
                  <div className="flex gap-2" data-id="element-3416">
                    {[{
                key: 'concise' as const,
                label: 'Conciso',
                icon: Zap
              }, {
                key: 'balanced' as const,
                label: 'Balanceado',
                icon: Scale
              }, {
                key: 'detailed' as const,
                label: 'Detallado',
                icon: ClipboardList
              }].map(({
                key,
                label,
                icon: DetailIcon
              }) => <button key={key} onClick={() => updateBehavior(selectedAgent.id, {
                detailLevel: key
              })} className={`flex-1 p-3 rounded-lg border text-center transition-all ${selectedAgent.behavior.detailLevel === key ? 'bg-indigo-900/20 border-indigo-500/50 text-indigo-300' : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'}`} data-id="element-3417">
                        <DetailIcon size={18} className="mx-auto mb-1" data-id="element-3418" />
                        <span className="text-xs font-medium" data-id="element-3419">{label}</span>
                      </button>)}
                  </div>
                </div>

                {/* Auto Actions Toggle */}
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3420">
                  <div className="flex items-center justify-between" data-id="element-3421">
                    <div className="flex items-center gap-2" data-id="element-3422">
                      <Zap size={16} className="text-amber-400" data-id="element-3423" />
                      <div data-id="element-3424">
                        <span className="text-sm font-medium text-slate-200 block" data-id="element-3425">
                          Acciones Automáticas
                        </span>
                        <span className="text-[10px] text-slate-500" data-id="element-3426">
                          Permitir al agente ejecutar acciones sin confirmación
                        </span>
                      </div>
                    </div>
                    <button onClick={() => updateBehavior(selectedAgent.id, {
                autoActions: !selectedAgent.behavior.autoActions
              })} className={`relative w-12 h-6 rounded-full transition-colors ${selectedAgent.behavior.autoActions ? 'bg-amber-600' : 'bg-slate-700'}`} data-id="element-3427">
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${selectedAgent.behavior.autoActions ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-3428" />
                    </button>
                  </div>
                </div>

                {/* Focus Areas */}
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800" data-id="element-3429">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3430">
                    <Target size={16} className="text-indigo-400" data-id="element-3431" />
                    <span className="text-sm font-medium text-slate-200" data-id="element-3432">
                      Áreas de Enfoque
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3433">
                    {['sprints', 'blockers', 'velocity', 'quality', 'deadlines', 'team-health', 'documentation', 'deployments'].map(area => {
                const isActive = selectedAgent.behavior.focusAreas.includes(area);
                return <button key={area} onClick={() => {
                  const newAreas = isActive ? selectedAgent.behavior.focusAreas.filter(a => a !== area) : [...selectedAgent.behavior.focusAreas, area];
                  updateBehavior(selectedAgent.id, {
                    focusAreas: newAreas
                  });
                }} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${isActive ? 'bg-indigo-900/30 text-indigo-300 border-indigo-500/50' : 'bg-slate-900 text-slate-500 border-slate-800 hover:border-slate-700'}`} data-id="element-3434">
                          {area}
                        </button>;
              })}
                  </div>
                </div>

                <Button onClick={handleSaveConfig} className="w-full gap-2" data-id="element-3435">
                  {saveSuccess ? <>
                      <Check size={16} data-id="element-3436" /> Guardado!
                    </> : <>
                      <Save size={16} data-id="element-3437" /> Guardar Comportamiento
                    </>}
                </Button>
              </>}
          </div>
        </div>}
    </div>;
}